import { format, isSameDay } from "date-fns";
import { generatePath, useLocation, useNavigate, useOutletContext, useParams } from "react-router";

import { GqlPlannerV2TransportationMethod } from "~/__generated__/graphql/types.ts";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { GaiaMapUIOverlay } from "~/modules/map/components/GaiaMapUIOverlay";
import { MapButtonFitBounds } from "~/modules/map/components/MapButtonFitBounds";
import { MapButtonToggleBaseMap } from "~/modules/map/components/MapButtonToggleBaseMap";
import RoutesLayerV2 from "~/modules/plannerV2/components/map-layers/RoutesLayerV2";
import { PLANNER_MAP_PADDING } from "~/modules/plannerV2/constants/map";
import { createSearchParamsString } from "~/shared/helpers/createSearchParamsString";

import { GaiaMapMarkerFieldworkerOnMap } from "../../../components/GaiaExpandableMapMarkerFieldworker/GaiaMapMarkerFieldworkerOnMap";
import { GaiaMapMarkerAreaOnMap } from "../../../components/GaiaMapMarkerArea/GaiaMapMarkerAreaOnMap";
import { WorkersLayerV2 } from "../../../components/map-layers/WorkersLayerV2";
import { usePlannerV2VisitActions } from "../../../hooks/usePlannerV2VisitActions";
import { usePlannerV2VisitDetailData } from "../../../hooks/usePlannerV2VisitDetailData";
import { usePlannerV2VisitFieldworkerData } from "../../../hooks/usePlannerV2VisitFieldworkerData";
import { PlannerV2ContextType } from "../types";
import { calculateVisitDetailScreenBounds } from "./calculateBounds";
import { PlannerV2VisitFilters } from "./components/PlannerV2VisitFilters";
import { VisitDetailPanel } from "./components/VisitDetailPanel";
import { usePlannerV2VisitFilters } from "./hooks/usePlannerV2VisitFilters";

export function PlannerV2VisitDetailScreen() {
    const { date, plannerAreaId, plannerVisitId } = useParams();

    if (!date || !plannerAreaId || !plannerVisitId) {
        throw new Error("Parameter `date`, `plannerAreaId`, or `plannerVisitId` is missing");
    }

    const navigate = useNavigate();
    const location = useLocation();
    const { plannerSharedLeftPanel, plannerSharedBottomRightPanel } = useOutletContext<PlannerV2ContextType>();

    const {
        error: visitError,
        loading: visitLoading,
        visit,
        possibleTimeslots,
        travelGroups,
        carpoolableTravelGroups,
        fieldworkersInAllTravelGroups,
        fieldworkerAssignedToVisit,
        routes,
        selectedTimeslotId,
        setSelectedTimeslotId,
        plannedOnDates,
    } = usePlannerV2VisitDetailData(date, plannerVisitId);

    const { apiParams: filters, filterValues } = usePlannerV2VisitFilters();
    const {
        availableFieldworkers,
        hoveredFieldworkerBaseData,
        hoveredFieldworkerAdditionalData,
        loadingHoveredFieldworkerAdditionalData,
        handleFieldworkerHover,
        clearHoveredFieldworker,
        error: fieldworkersError,
    } = usePlannerV2VisitFieldworkerData({
        onDate: date,
        plannerVisitId,
        timeslotId: selectedTimeslotId,
        filters,
    });

    const {
        addFieldworkerToShift,
        addFieldworkerToTravelGroup,
        cancelTravelGroup,
        cancelShiftInTravelGroup,
        isAddingAnyFieldworker,
        isCancelingShiftId,
        isCancelingGroupId,
        fieldworkerAddingError,
    } = usePlannerV2VisitActions({
        date,
        visitId: plannerVisitId,
        selectedTimeslotId,
        allowIfUnavailable: filterValues.filterFieldworkerIncludeUnavailable,
    });

    const bounds = calculateVisitDetailScreenBounds(visit?.plannerArea.location);

    const isNotPlannedBefore = plannedOnDates.length < 1;
    const visitStartTime = visit?.startTime ? parseStringToDate(visit.startTime) : undefined;
    const isPlannedOnCurrentDate = visitStartTime ? isSameDay(visitStartTime, date) : false;
    const isPreviouslyPlannedOnCurrentDate = plannedOnDates.some(x => isSameDay(x.date, date)) && !isPlannedOnCurrentDate;

    const isFocusedOnSpecificDate =
        isNotPlannedBefore || isPlannedOnCurrentDate || (location.state?.narrowed === true && !isPreviouslyPlannedOnCurrentDate);

    const generateVisitDateHref = (day: Date) =>
        generatePath("/planner/:date/areas/:plannerAreaId/visits/:plannerVisitId", {
            date: format(day, "yyyy-MM-dd"),
            plannerAreaId,
            plannerVisitId,
        });

    return (
        <>
            <GaiaMapUIOverlay
                leftPanel={
                    <>
                        {plannerSharedLeftPanel}
                        <PlannerV2VisitFilters />
                    </>
                }
                rightPanel={
                    <VisitDetailPanel
                        travelGroups={travelGroups ?? []}
                        visit={visit}
                        assignedFieldworkers={fieldworkerAssignedToVisit ?? []}
                        possibleTimeslots={possibleTimeslots}
                        selectedTimeslotId={selectedTimeslotId}
                        onTimeslotChange={setSelectedTimeslotId}
                        date={date}
                        onTravelGroupCancelClick={travelGroupId => cancelTravelGroup(travelGroupId)}
                        onShiftInTravelGroupCancelClick={({ shiftId, travelGroupId }) =>
                            cancelShiftInTravelGroup({ shiftId, travelGroupId })
                        }
                        travelGroupOpenHref={plannerTravelGroupId => ({
                            pathname: generatePath("travel-groups/:plannerTravelGroupId", { plannerTravelGroupId }),
                            search: createSearchParamsString({
                                filterFieldworkerIncludeUnavailable: String(filterValues.filterFieldworkerIncludeUnavailable),
                            }),
                        })}
                        loadingError={visitError || fieldworkersError}
                        loading={visitLoading && visit === undefined}
                        isCancelingShiftId={isCancelingShiftId}
                        isCancelingGroupId={isCancelingGroupId}
                        isFocusedOnSpecificDate={isFocusedOnSpecificDate}
                        isOnPriorDate={isPreviouslyPlannedOnCurrentDate}
                        plannedOnDates={plannedOnDates}
                        onSelectPlannedOnDate={value => {
                            navigate(generateVisitDateHref(value), { state: { narrowed: true } });
                        }}
                        backHref=".."
                        selectedDate={parseStringToDate(date)}
                    />
                }
                bottomRightPanel={plannerSharedBottomRightPanel}
                bottomBar={
                    <>
                        <MapButtonFitBounds bounds={bounds} padding={PLANNER_MAP_PADDING} />
                        <MapButtonToggleBaseMap />
                    </>
                }
            />

            <WorkersLayerV2
                availableWorkers={availableFieldworkers ?? []}
                assignedWorkers={fieldworkersInAllTravelGroups ?? []}
                onWorkerHover={handleFieldworkerHover}
            />
            <RoutesLayerV2 routes={routes ?? []} />

            {visit?.plannerArea && (
                <GaiaMapMarkerAreaOnMap
                    selected
                    hasStagedChanges={visit.hasStagedChanges}
                    coordinates={visit.plannerArea.location.coordinates}
                    plannerStatusBucket={visit.plannerArea.plannerStatusBucket}
                />
            )}

            {hoveredFieldworkerBaseData && (
                <GaiaMapMarkerFieldworkerOnMap
                    fieldworker={hoveredFieldworkerBaseData}
                    selectedDate={date}
                    coordinates={hoveredFieldworkerBaseData.location.coordinates}
                    onMouseLeave={clearHoveredFieldworker}
                    loading={loadingHoveredFieldworkerAdditionalData}
                    availabilityByDate={hoveredFieldworkerAdditionalData?.availabilityByDate}
                    availableTransportationMethods={
                        hoveredFieldworkerAdditionalData.travelDistance?.availableTransportationMethods
                    }
                    onAddToVisit={async (transportationMethod: GqlPlannerV2TransportationMethod) => {
                        const success = await addFieldworkerToShift(hoveredFieldworkerBaseData.id, transportationMethod);

                        if (success) {
                            clearHoveredFieldworker();
                        }
                    }}
                    onAddToCarpool={async ({ plannerTravelGroupId, fieldworkerId }) => {
                        const success = await addFieldworkerToTravelGroup({ plannerTravelGroupId, fieldworkerId });

                        if (success) {
                            clearHoveredFieldworker();
                        }
                    }}
                    isAssigned={false}
                    isBeingAdded={isAddingAnyFieldworker}
                    availableTravelGroups={carpoolableTravelGroups ?? []}
                    error={fieldworkerAddingError}
                />
            )}
        </>
    );
}
